import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePhotographerEvents } from '@context/PhotographerEventsContext';
import { useOnboardingReturn } from '@hooks/useOnboardingReturn';
import type { EventTypeOption } from '@/types/photographer';

const EVENT_TYPES: EventTypeOption[] = [
  'wedding', 'corporate', 'portrait', 'family', 'graduation', 'birthday', 'other',
];

interface Props {
  open: boolean;
  onClose: () => void;
}

const CreateEventModal: React.FC<Props> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const returnToOnboarding = useOnboardingReturn();
  const { createEvent } = usePhotographerEvents();
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [eventType, setEventType] = useState<EventTypeOption>('wedding');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  // Move focus into the dialog when it opens.
  useEffect(() => {
    if (open) firstFieldRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const event = await createEvent({
        title: title.trim(),
        eventDate: new Date(date).toISOString(),
        eventType,
      });
      onClose();
      // During onboarding, return to the dashboard wizard so progress shows;
      // otherwise continue to the new event so the user can add photos.
      if (!returnToOnboarding()) {
        navigate(`/dashboard/events/${event.id}`);
      }
    } catch {
      setError('Could not create the event. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-event-title"
        onKeyDown={onKeyDown}
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
      >
        <h2 id="create-event-title" className="mb-4 text-lg font-semibold text-gray-900">Create event</h2>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="event-name" className="block text-sm font-medium text-gray-700">Event name</label>
            <input id="event-name" ref={firstFieldRef} value={title} onChange={e => setTitle(e.target.value)} required
                   className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
          </div>
          <div>
            <label htmlFor="event-date" className="block text-sm font-medium text-gray-700">Date</label>
            <input id="event-date" type="date" value={date} onChange={e => setDate(e.target.value)} required
                   className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
          </div>
          <div>
            <label htmlFor="event-type" className="block text-sm font-medium text-gray-700">Type</label>
            <select id="event-type" value={eventType}
                    onChange={e => setEventType(e.target.value as EventTypeOption)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2">
              {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose}
                    className="rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
            <button type="submit" disabled={saving || !title.trim() || !date}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Creating…' : 'Create event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEventModal;
